// Same-machine cart sync between POS and CFD windows.
//
// The CFD window shows the customer-facing view of the cart in real time as
// the clerk builds it. With in-memory carts the BE doesn't have the cart
// until commit, so the BE walkie-talkie path can't push it -- we sync FE↔FE.
//
// Transport selection (in order of preference):
//   1. BroadcastChannel API: native, sub-ms latency, same-origin only.
//   2. localStorage `storage` event: universal fallback, slightly slower,
//      serializes through disk.
//
// Both use the same shape; consumers don't care which is active. The channel
// is a singleton per import; multiple subscribers are fine.
//
// Why not WebSocket-to-localhost? Requires a server process, port discovery,
// firewall rules. Not worth it when the windows are same-origin (the common
// case for the Windows wrapper).
//
// Tests: `cartChannel.spec.ts` (TBD) -- mock window/localStorage and assert
// publish-then-subscribe round-trips.

import type {
  Cart, CartCustomerDisplay, CartLine, CartLoyalty, CartPreviewStatus, PreviewTotals,
} from './cart'
import type { Product } from '@/types'
import { getProductTypeName } from './productType'

// ---------------------------------------------------------------------------
// Wire format
// ---------------------------------------------------------------------------

/** Payload shape sent over the channel. Keep small + serializable. */
export interface CartChannelMessage {
  type:
    | 'cart.updated'         // POS → CFD: full cart snapshot
    | 'cart.cleared'         // POS → CFD: cart was cleared (commit/cancel)
    | 'cart.request_state'   // CFD → POS: I just opened, send current state
    | 'product.shown'        // POS → CFD: render this product on the right
    | 'product.hidden'       // POS → CFD: revert right panel to welcome state
    | 'payment.approved'     // POS → CFD: receipt fully paid — celebrate
  /** Wall-clock at sender; lets receivers drop out-of-order frames. */
  ts: number
  /** Sequence number from the sender. Receivers track latest seen. */
  seq?: number
  /** For cart.updated, the cart's render-relevant slice. */
  cart?: CartSnapshot
  /** For product.shown, the product slice the CFD renders. */
  product?: FeaturedProductPayload
}

/** Display projection of a single payment on the CFD. Mirrors the legacy
 *  template's `payments_dict` rows — we only ship what the CFD renders
 *  (label + amount). Voided payments are filtered out server-side, so any
 *  row in this list is a live partial. */
export interface CartPaymentDisplay {
  /** Cashier-visible label, e.g. "Cash", "Visa", "Gift Card". Falls back
   *  to "Payment" when the BE doesn't supply one. */
  payment_method: string | null
  /** Tendered amount as a positive string. The CFD renders it as a
   *  negative (debit-style) value to mirror the legacy template.
   *  Tip-INCLUSIVE for integrated-terminal tips (mirrors jamount). */
  amount: string
  /** Integrated-terminal tip portion of `amount`; '0' when untipped.
   *  Lets the CFD label the payment "(incl. $X tip)" so the charged
   *  amount exceeding the order total is explained to the customer. */
  tip_amount?: string
}

/** Slice of Cart we ship over the wire. We deliberately don't send the
 *  whole zustand state -- only what the CFD renders. Keeps message size
 *  bounded and decouples the wire format from internal store fields. */
export interface CartSnapshot {
  customer_id: number | null
  customer: CartCustomerDisplay | null
  exempt_taxes: boolean
  lines: CartLine[]
  totals: PreviewTotals | null
  previewStatus: CartPreviewStatus
  /** Partial payments already tendered against the cart's backing receipt.
   *  Null when no receipt exists yet (cart still in pre-commit state) or
   *  when no partial has landed. Empty array means a receipt exists but
   *  the cashier hasn't tendered anything yet. */
  partialPayments: CartPaymentDisplay[] | null
  /** Remaining balance after partial payments. String for currency
   *  precision; matches the BE receipt's `amount_to_pay`. Null when no
   *  partial-payment context exists. */
  partialAmountToPay: string | null
  /** Loyalty projection — rewards-available flag + staged redemption credit.
   *  Null when no customer is attached. Drives the CFD's "Rewards Available"
   *  badge and "Loyalty −$X" / "To collect" rows. */
  loyalty: CartLoyalty | null
}

/** Slice of a Product we ship to the CFD when the cashier opens a product
 *  modal. Mirrors the legacy `display_waio.html` show-product payload —
 *  enough for the CFD to render image, brand, strain, prices, weight chip,
 *  and description. We deliberately don't ship the entire `Product` object
 *  because it carries a lot of fields the CFD doesn't need (margins,
 *  schedule_id, ar_*, supplier_*, etc.).
 *
 *  All numeric fields are normalized to `string` so the wire format stays
 *  consistent across number/string variants the v2 API returns. */
export interface FeaturedProductPayload {
  product_id: number
  cspcid: string | null
  /** Product title/headline. Despite the name, this carries the
   *  display_name (with `description` and `name` as fallbacks) — the
   *  upstream `Product.description` field is the product name, not the
   *  marketing copy. The actual long-form marketing paragraph lives
   *  in `product_info_text` below. */
  description: string
  /** Long-form marketing/info paragraph the CFD renders in its
   *  Description block — same source the modal uses
   *  (`product.product_info_text`). Null when the catalog doesn't
   *  carry one for this product, in which case the CFD hides the
   *  Description block entirely (instead of falling back to the title
   *  and repeating it). */
  product_info_text: string | null
  brand_name: string | null
  /** Resolved image URL (already `http(s)://…` or `/s/<media>` — the CFD
   *  renders verbatim, no additional prefixing). */
  image: string | null
  is_cannabis: boolean
  /** Free-form strain from the catalog. CFD applies its own
   *  meaningful-strain predicate to suppress empty / `-` / `n/a`. */
  strain: string | null
  /** Strain classification (Sativa / Indica / Hybrid / Blend / etc.) —
   *  POS resolves via `getProductTypeName(product)` (which already
   *  strips "Dominant"). The CFD renders this as the colored pill on
   *  the left of the chip row, mirroring the POS product card's badge.
   *  Empty string when the catalog doesn't carry a product_type. */
  product_type_name: string | null
  /** Catalog regular price (the "Market Price" column in the legacy
   *  template). String for currency precision. */
  regular_price: string | null
  /** Customer-tier price when one applies, else null. */
  price2: string | null
  /** Sale price under an active scheduled discount, when applicable. */
  scheduled_discount_price: string | null
  /** Per-unit price the customer will/does pay for this product. POS
   *  fills this with whichever is most authoritative for the moment:
   *    1. /cart/preview's resolved unit price when the product is already
   *       in the cart (server-priced; reflects active schedules and
   *       customer-tier eligibility exactly).
   *    2. FE-computed `effectiveUnitPrice(product, customer)` otherwise
   *       (modal open before add, grid + click before preview returns).
   *  CFD renders this verbatim — no precedence logic in the receiver,
   *  so the customer screen stays in lockstep with the cashier's
   *  Add-to-Cart price. Null only on payloads built without a price
   *  (defensive fallback; in practice POS always supplies one). */
  effective_unit_price: string | null
  /** Display unit name (e.g. "7g", "1x0.5g") for the weight chip. */
  unit_name: string | null
  /** Cannabis grams (for the badge); null on non-cannabis. */
  mj_grams: string | null
  /** THC / CBD percentages; strings for the same currency-precision
   *  reasons. Null on non-cannabis. */
  test_thc: string | null
  test_cbd: string | null
  /** Unit of test_thc/test_cbd; see lib/potency. */
  test_units?: string | null
}

/** Build a `FeaturedProductPayload` from a Product + the resolved
 *  per-unit price the customer will/does pay. Caller picks the right
 *  source: /cart/preview resolved price when the product is in the
 *  cart, else `effectiveUnitPrice(product, selectedCustomer)`. The CFD
 *  renders this verbatim. */
export function toFeaturedProduct(
  product: Product,
  effectivePrice: number | null = null,
): FeaturedProductPayload {
  const numToStr = (v: unknown): string | null => {
    if (v === null || v === undefined || v === '') return null
    const n = typeof v === 'number' ? v : parseFloat(String(v))
    return Number.isFinite(n) ? n.toString() : null
  }
  const strOrNull = (v: unknown): string | null => {
    if (v === null || v === undefined) return null
    const s = String(v).trim()
    return s === '' ? null : s
  }

  const image =
    strOrNull(product.image_url)
    ?? strOrNull(product.thumbnail_url)
    ?? strOrNull(product.image)
    ?? strOrNull((product as { main_image?: string | null }).main_image)
    ?? null

  const unitName =
    strOrNull(product.product_unit)
    ?? strOrNull((product as { unit?: { name?: string } }).unit?.name)
    ?? null

  // scheduled_discount.discount_price — only meaningful when the schedule
  // applies and the price is below regular. CFD does the comparison, we
  // just pass the candidate through.
  const sd = (product as { scheduled_discount?: { discount_price?: unknown } | null })
    .scheduled_discount
  const scheduledDiscountPrice =
    sd && typeof sd === 'object' ? numToStr(sd.discount_price) : null

  return {
    product_id: product.id,
    cspcid: strOrNull(product.cspcid),
    description: strOrNull(product.display_name) ?? strOrNull(product.description) ?? strOrNull(product.name) ?? `Product ${product.id}`,
    // Only the genuine marketing paragraph — never fall back to
    // product.description, which is the product name and would render
    // as a duplicated title in the CFD's Description block.
    product_info_text: strOrNull(product.product_info_text),
    brand_name: strOrNull(product.brand_name) ?? strOrNull(product.vendor_name) ?? strOrNull(product.brand),
    image,
    is_cannabis: product.is_cannabis === true,
    strain: strOrNull(product.strain) ?? strOrNull(product.strain_type),
    product_type_name: strOrNull(getProductTypeName(product)),
    regular_price: numToStr(product.regular_price ?? product.price),
    price2: numToStr(product.price2),
    scheduled_discount_price: scheduledDiscountPrice,
    effective_unit_price: numToStr(effectivePrice),
    unit_name: unitName,
    mj_grams: numToStr(product.mj_grams),
    test_thc: numToStr(product.test_thc ?? product.thc_percent),
    test_cbd: numToStr(product.test_cbd ?? product.cbd_percent),
    test_units: product.test_units ?? null,
  }
}

/** Project a full Cart down to the wire snapshot. */
export function toSnapshot(cart: Cart): CartSnapshot {
  return {
    customer_id: cart.customer_id,
    customer: cart.customer,
    exempt_taxes: cart.exempt_taxes,
    lines: cart.lines.map((l) => ({
      localId: l.localId,
      product_id: l.product_id,
      qty: l.qty,
      lot_number: l.lot_number,
      manual_price: l.manual_price,
      priced: l.priced,
      state: l.state,
      display: l.display,
      // Line-total cashier discount (signed-positive). The CFD needs
      // this to back out a post-discount line total when /cart/preview
      // hasn't (or can't) confirm priced.subtotal — without it the
      // CFD shows the catalog price while the cart shows post-discount.
      discount_amount: l.discount_amount ?? null,
    })),
    totals: cart.preview?.totals ?? null,
    previewStatus: cart.previewStatus,
    partialPayments: cart.partialPayments,
    partialAmountToPay: cart.partialAmountToPay,
    loyalty: cart.loyalty,
  }
}

// ---------------------------------------------------------------------------
// Transport: BroadcastChannel preferred, localStorage fallback.
// ---------------------------------------------------------------------------

const CHANNEL_NAME = 'waio.cart'
const STORAGE_KEY = '__waio_cart_channel__'

type Listener = (msg: CartChannelMessage) => void

interface Transport {
  publish(msg: CartChannelMessage): void
  subscribe(listener: Listener): () => void
  /** True if the transport actually wired up; false in non-browser environments
   *  (SSR, tests without window) so callers can no-op cleanly. */
  readonly active: boolean
  /** Human-readable transport name -- for diagnostics in dev tools. */
  readonly kind: 'broadcast' | 'localstorage' | 'noop'
}

function makeBroadcastTransport(): Transport | null {
  if (typeof BroadcastChannel === 'undefined') return null
  let bc: BroadcastChannel
  try {
    bc = new BroadcastChannel(CHANNEL_NAME)
  } catch {
    // Some embedded browsers (older WebView2) report BroadcastChannel as
    // defined but throw on construction. Fall through to localStorage.
    return null
  }
  const listeners = new Set<Listener>()
  bc.onmessage = (ev) => {
    const msg = ev.data as CartChannelMessage
    if (msg && typeof msg === 'object') {
      listeners.forEach((l) => l(msg))
    }
  }
  return {
    active: true,
    kind: 'broadcast',
    publish: (msg) => bc.postMessage(msg),
    subscribe: (listener) => {
      listeners.add(listener)
      return () => {
        listeners.delete(listener)
      }
    },
  }
}

function makeLocalStorageTransport(): Transport | null {
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
    return null
  }
  const listeners = new Set<Listener>()
  const handler = (ev: StorageEvent) => {
    if (ev.key !== STORAGE_KEY || ev.newValue === null) return
    try {
      const msg = JSON.parse(ev.newValue) as CartChannelMessage
      listeners.forEach((l) => l(msg))
    } catch {
      // Ignore malformed payloads; another sender may have stomped the slot.
    }
  }
  window.addEventListener('storage', handler)
  return {
    active: true,
    kind: 'localstorage',
    publish: (msg) => {
      try {
        // Setting the same value twice doesn't trigger `storage` in some
        // browsers, so we vary the slot key with `seq`/`ts` to ensure the
        // event always fires.
        localStorage.setItem(STORAGE_KEY, JSON.stringify(msg))
      } catch {
        // localStorage can throw in private mode / over quota. Silent fail
        // -- CFD will catch up on the next state-request cycle.
      }
    },
    subscribe: (listener) => {
      listeners.add(listener)
      return () => {
        listeners.delete(listener)
      }
    },
  }
}

function makeNoopTransport(): Transport {
  return {
    active: false,
    kind: 'noop',
    publish: () => undefined,
    subscribe: () => () => undefined,
  }
}

// Singleton -- pick the best available transport at module load.
let _transport: Transport | null = null
function getTransport(): Transport {
  if (_transport === null) {
    _transport =
      makeBroadcastTransport() ??
      makeLocalStorageTransport() ??
      makeNoopTransport()
  }
  return _transport
}

/** Diagnostic accessor; returns 'broadcast' | 'localstorage' | 'noop'. */
export function activeTransportKind(): Transport['kind'] {
  return getTransport().kind
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

let _seq = 0

/** Publish a cart update. POS calls this from its store subscription. */
export function broadcastCart(cart: Cart): void {
  _seq = (_seq + 1) | 0
  getTransport().publish({
    type: 'cart.updated',
    ts: Date.now(),
    seq: _seq,
    cart: toSnapshot(cart),
  })
}

/** Publish a "cart was cleared" signal. POS calls this on commit/cancel. */
export function broadcastCleared(): void {
  _seq = (_seq + 1) | 0
  getTransport().publish({
    type: 'cart.cleared',
    ts: Date.now(),
    seq: _seq,
  })
}

/** Publish "render this product on the right". POS calls this when the
 *  product modal opens (and re-fires on customer change so tier pricing
 *  updates). Re-firing with the same product is fine — CFD just snaps
 *  to whatever the latest payload says. */
export function broadcastProductShown(product: FeaturedProductPayload): void {
  _seq = (_seq + 1) | 0
  getTransport().publish({
    type: 'product.shown',
    ts: Date.now(),
    seq: _seq,
    product,
  })
}

/** Publish "stop showing the featured product" — CFD reverts to the
 *  welcome state. POS calls this when the product modal closes. */
export function broadcastProductHidden(): void {
  _seq = (_seq + 1) | 0
  getTransport().publish({
    type: 'product.hidden',
    ts: Date.now(),
    seq: _seq,
  })
}

/** CFD calls this on window open to pull current state from POS. POS will
 *  reply via `broadcastCart` if a subscriber is hooked. */
export function requestCartState(): void {
  _seq = (_seq + 1) | 0
  getTransport().publish({
    type: 'cart.request_state',
    ts: Date.now(),
    seq: _seq,
  })
}

/** POS calls this when a receipt is fully paid (amount_to_pay reaches 0,
 *  not on partial / split-tender intermediate steps). The CFD fires a
 *  one-shot confetti animation. Refunds intentionally do NOT broadcast
 *  this — getting money back is not a celebration moment. */
export function broadcastPaymentApproved(): void {
  _seq = (_seq + 1) | 0
  getTransport().publish({
    type: 'payment.approved',
    ts: Date.now(),
    seq: _seq,
  })
}

/** Subscribe to channel messages. Used by both sides:
 *  - POS subscribes to 'cart.request_state' so it can rebroadcast.
 *  - CFD subscribes to 'cart.updated' / 'cart.cleared' to update its render.
 *
 *  Returns an unsubscribe function. Multiple calls per process are fine.
 */
export function onCartChannelMessage(listener: Listener): () => void {
  return getTransport().subscribe(listener)
}

// ---------------------------------------------------------------------------
// POS-side wiring. Call once from the POS app boot file. Idempotent --
// subsequent calls are no-ops because the singleton transport doesn't double-
// register.
// ---------------------------------------------------------------------------

let _posWired = false

/**
 * Wire the POS window to broadcast cart updates and reply to CFD state
 * requests. Call this once from `main.tsx` after the cart store is imported.
 *
 * `getCart` is a snapshot function -- typically `() => useCartStore.getState()`.
 * Passing it instead of importing the store directly keeps this file
 * dependency-free of zustand and easier to test.
 *
 * `subscribeToCart` registers a listener that fires whenever the cart
 * changes -- typically `useCartStore.subscribe`. We use it to broadcast
 * on every change.
 *
 * Returns a teardown function that disconnects everything (useful for HMR
 * in dev so we don't pile up subscribers across reloads).
 */
/** Heartbeat interval for empty-cart re-broadcasts. The CFD is otherwise
 *  fully push-based; if a `cart.cleared` frame is dropped (storage event
 *  not delivered, CFD mounted late, transient transport hiccup) the stale
 *  cart can persist until the next POS mutation. Re-emitting the cleared
 *  signal at this cadence is a cheap backstop — same-origin BroadcastChannel
 *  delivery is essentially free and the message has no payload beyond a ts
 *  + seq. We only heartbeat when the cart is empty: an active cart already
 *  emits on every change via the store subscription, so re-broadcasting
 *  there would be noise. */
const EMPTY_CART_HEARTBEAT_MS = 5_000

export function setupPosCartBroadcast(args: {
  getCart: () => Cart
  subscribeToCart: (listener: () => void) => () => void
}): () => void {
  if (_posWired) {
    // Calling this twice is a bug; warn loudly in dev so HMR / boot order
    // issues are visible. Production silently no-ops to avoid disruption.
    if (typeof console !== 'undefined') {
      console.warn('setupPosCartBroadcast called twice; ignoring.')
    }
    return () => undefined
  }
  _posWired = true

  // Empty cart on the POS side means "no active transaction" — either
  // initial boot or a just-completed sale. Broadcasting `cart.cleared`
  // (instead of `cart.updated` with empty lines) tells the CFD to wipe
  // the snapshot AND the featured product, so the next customer sees
  // the welcome screen rather than the previous customer's last
  // featured item.
  const broadcastCurrentState = () => {
    const cart = args.getCart()
    if (cart.lines.length === 0) {
      broadcastCleared()
    } else {
      broadcastCart(cart)
    }
  }

  const unsubStore = args.subscribeToCart(() => {
    broadcastCurrentState()
  })

  const unsubChannel = onCartChannelMessage((msg) => {
    if (msg.type === 'cart.request_state') {
      // Reply with the current snapshot so the CFD can re-hydrate.
      broadcastCurrentState()
    }
  })

  // Empty-cart heartbeat. See EMPTY_CART_HEARTBEAT_MS comment for why.
  const heartbeat: ReturnType<typeof setInterval> | null =
    typeof setInterval !== 'undefined'
      ? setInterval(() => {
          const cart = args.getCart()
          if (cart.lines.length === 0) {
            broadcastCleared()
          }
        }, EMPTY_CART_HEARTBEAT_MS)
      : null

  return () => {
    unsubStore()
    unsubChannel()
    if (heartbeat !== null) clearInterval(heartbeat)
    _posWired = false
  }
}
